import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  GfsOffer,
  GfsOfferCalculationUpdate,
  GfsOfferContent,
  GfsOfferDocumentSnapshot,
  PutGfsOfferRequest,
} from "@/services/backend/gfs/offer/types";
import {
  updateGfsOfferCache,
  useGfsOfferPutMutation,
} from "@/services/backend/gfs/offer/api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import type { RtkQueryMutation } from "@/shared/lib/debounce/debounce";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import { newAddress } from "@/services/backend/addresses/types/address";
import {
  buildGfsOfferSubject,
  defaultGfsOfferSubject,
} from "@/services/backend/gfs/offer/lib/target-organism-title";

type GfsOfferEditorContextValue = {
  offer: GfsOffer;
  putRequest: PutGfsOfferRequest;
  updateContent: (patch: Partial<GfsOfferContent>) => void;
  updateCalculation: (calculation: GfsOfferCalculationUpdate) => void;
  updateOfferDocument: (offerDocument: GfsOfferDocumentSnapshot) => void;
  replaceOffer: (offer: GfsOffer) => void;
  saveState: ReturnType<typeof useGfsOfferPutMutation>[1];
};

const GfsOfferEditorContext = createContext<GfsOfferEditorContextValue | null>(
  null,
);

export function GfsOfferEditorContextProvider({
  offer,
  children,
}: {
  offer: GfsOffer;
  children: ReactNode;
}) {
  const initialPutRequest = useMemo(() => toPutRequest(offer), [offer]);
  const dispatch = useAppDispatch();
  const latestRequestRef = useRef(initialPutRequest);
  const offerRef = useRef(offer);
  offerRef.current = offer;
  const hasPendingRequestRef = useRef(false);

  useEffect(() => {
    if (!hasPendingRequestRef.current) {
      latestRequestRef.current = initialPutRequest;
    }
  }, [initialPutRequest]);

  const [saveMutation, saveState] = useGfsOfferPutMutation();
  const typedSaveMutation = [
    saveMutation,
    saveState,
  ] as unknown as RtkQueryMutation<PutGfsOfferRequest, GfsOffer>;
  const [setOffer] = useOptimisticDebouncedMutation<
    GfsOffer,
    PutGfsOfferRequest,
    GfsOffer
  >(initialPutRequest, updateGfsOfferCache, typedSaveMutation, {
    delay: 500,
    skip: offer.immutable,
    toastError: true,
    onSuccess: (_result, request) => {
      hasPendingRequestRef.current = false;
      latestRequestRef.current = request;
    },
    onError: () => {
      hasPendingRequestRef.current = false;
    },
  });

  const updateRequest = useCallback(
    (nextRequest: PutGfsOfferRequest) => {
      hasPendingRequestRef.current = true;
      latestRequestRef.current = nextRequest;
      setOffer(nextRequest, toOptimisticOffer(offerRef.current, nextRequest));
    },
    [setOffer],
  );

  const replaceOffer = useCallback(
    (nextOffer: GfsOffer) => {
      hasPendingRequestRef.current = false;
      latestRequestRef.current = toPutRequest(nextOffer);
      dispatch(updateGfsOfferCache(nextOffer));
    },
    [dispatch],
  );

  const updateContent = useCallback(
    (patch: Partial<GfsOfferContent>) => {
      const baseRequest = latestRequestRef.current;
      updateRequest({
        ...baseRequest,
        content: { ...baseRequest.content, ...patch },
      });
    },
    [updateRequest],
  );

  const updateCalculation = useCallback(
    (calculation: GfsOfferCalculationUpdate) => {
      updateRequest({ ...latestRequestRef.current, calculation });
    },
    [updateRequest],
  );

  const updateOfferDocument = useCallback(
    (offerDocument: GfsOfferDocumentSnapshot) => {
      updateRequest({ ...latestRequestRef.current, offerDocument });
    },
    [updateRequest],
  );

  const value: GfsOfferEditorContextValue = {
    offer,
    putRequest: initialPutRequest,
    updateContent,
    updateCalculation,
    updateOfferDocument,
    replaceOffer,
    saveState,
  };

  return (
    <GfsOfferEditorContext.Provider value={value}>
      {children}
    </GfsOfferEditorContext.Provider>
  );
}

function toOptimisticOffer(
  offer: GfsOffer,
  request: PutGfsOfferRequest,
): GfsOffer {
  return {
    ...offer,
    content: request.content,
    offerDocument: request.offerDocument ?? offer.offerDocument,
    calculation: {
      ...offer.calculation,
      measureSettings: request.calculation.measureSettings,
      pricingSettings: request.calculation.pricingSettings,
      measures: request.calculation.measures,
      pricing: {
        ...offer.calculation.pricing,
        items: request.calculation.priceItems,
      },
    },
  };
}

export function useGfsOfferEditorContext() {
  const context = useContext(GfsOfferEditorContext);
  if (!context) {
    throw new Error("GFS offer editor context is missing");
  }
  return context;
}

function toPutRequest(offer: GfsOffer): PutGfsOfferRequest {
  const calculation = {
    measureSettings: offer.calculation.measureSettings,
    pricingSettings: offer.calculation.pricingSettings,
    measures: offer.calculation.measures,
    priceItems: offer.calculation.pricing.items,
  };

  return {
    id: offer.id,
    companyId: offer.companyId,
    customerId: offer.customerId,
    content: normalizeContent(offer.content, offer.calculation.targetOrganisms),
    offerDocument: offer.offerDocument,
    calculation,
  };
}

function normalizeContent(
  content: GfsOfferContent,
  calculationTargetOrganisms: string[],
): GfsOfferContent {
  const expertTrainingPrice =
    content.expertTraining?.enabled === false
      ? 0
      : Math.max(0, content.expertTraining?.price ?? 0);
  const targetOrganismSelection = {
    pestGroups: content.targetOrganismSelection?.pestGroups ?? [],
    pestCategories: content.targetOrganismSelection?.pestCategories ?? [],
    pestIndicators: content.targetOrganismSelection?.pestIndicators ?? [],
  };
  const targetOrganisms = content.targetOrganisms ?? [];
  const subject = content.subject ?? "";
  const automaticSubject = buildGfsOfferSubject(
    targetOrganismSelection,
    targetOrganisms.length ? targetOrganisms : calculationTargetOrganisms,
  );
  const subjectManuallySet =
    content.subjectManuallySet ??
    (subject.trim() !== "" &&
      subject.trim() !== defaultGfsOfferSubject &&
      subject.trim() !== automaticSubject);

  return {
    subject: subjectManuallySet ? subject : automaticSubject,
    subjectManuallySet,
    salutation: content.salutation ?? "",
    coverLetter: content.coverLetter ?? "",
    contact: content.contact ?? "",
    services: content.services ?? "",
    serviceProgram: content.serviceProgram ?? "",
    targetOrganisms,
    targetOrganismSelection,
    processAndInvestment: content.processAndInvestment ?? "",
    expertTraining: {
      enabled: expertTrainingPrice > 0,
      participants: Math.max(0, content.expertTraining?.participants ?? 2),
      price: expertTrainingPrice,
    },
    closingText: content.closingText ?? "",
    notes: content.notes ?? "",
    postalAddress: { ...newAddress(), ...(content.postalAddress ?? {}) },
    postalAddressContactRole: content.postalAddressContactRole ?? "",
    serviceAddress: { ...newAddress(), ...(content.serviceAddress ?? {}) },
    objectBuildings: content.objectBuildings ?? [],
    objectBuildingsSet: content.objectBuildingsSet,
    representative: {
      name: content.representative?.name ?? "",
      email: content.representative?.email ?? "",
      mobilephone: content.representative?.mobilephone ?? "",
      phone: content.representative?.phone ?? "",
    },
    representativeRole: content.representativeRole ?? "",
    representativePhoneType:
      content.representativePhoneType === "mobile" ? "mobile" : "phone",
    caseData: {
      customerResponsibles:
        content.caseData?.customerResponsibles?.map((person) => ({
          name: person.name ?? "",
          phone: person.phone ?? "",
          role: person.role ?? "",
        })) ?? [],
      responsibleName: content.caseData?.responsibleName ?? "",
      measure: content.caseData?.measure ?? "",
      infestation: content.caseData?.infestation ?? "",
      position: content.caseData?.position ?? "",
      situation: content.caseData?.situation ?? "",
      scope: content.caseData?.scope ?? "",
      timeWindow: content.caseData?.timeWindow ?? "",
      appointment: content.caseData?.appointment ?? "",
      customerPreparation: content.caseData?.customerPreparation ?? "",
      customerSupport: content.caseData?.customerSupport ?? "",
      products: content.caseData?.products ?? "",
      importantNotes: content.caseData?.importantNotes ?? "",
    },
  };
}
